import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { useGetArtefactScope } from '@modules/common/hooks';
import { DASHBOARD_NAME_MAX_LENGTH } from '@modules/dashboard/constants';
import {
  getAnalysisTypeDisplayName,
  getDashboardLink,
  getEmojiFromDashboard,
  getShortMetadataForDashboard,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardHeader } from '@modules/dashboard/types';
import { Box, Divider } from '@mui/material';
import { FormEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { OpenDashboardLink } from '../OpenDashboardLink';
import { CreateNewDashboardDialogActions } from './CreateNewDashboardDialogActions';
import { CreateNewDashboardDialogContentForm } from './CreateNewDashboardDialogContentForm';
import { CreateNewDashboardDialogTitle } from './CreateNewDashboardDialogTitle';

interface CreateNewDashboardDialogContentProps {
  onClose: () => void;
}

export const CreateNewDashboardDialogContent = ({
  onClose,
}: CreateNewDashboardDialogContentProps) => {
  const analysisName = useAnalysisStore(({ title }) => title);
  const analysisID = useAnalysisStore(({ id }) => id);
  const useCase = useAnalysisStore(({ useCase }) => useCase);

  const [newDashboard, setNewDashboard] = useState<DashboardHeader>({
    name: '',
    emoji: getEmojiFromDashboard(),
  });

  const artefactScope = useGetArtefactScope();

  const { showSuccessNotification } = useNotificationContext();

  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const shortMetadata = getShortMetadataForDashboard({
    dashboardEmoji: newDashboard?.emoji,
  });

  const { upsertDashboard, loading } = useUpsertDashboard();

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();

    const response = await upsertDashboard({
      dashboardProps: {
        title: newDashboard.name,
        scope: artefactScope,
        analysisIDs: [analysisID],
        shortMetadata,
        metadata: '',
      },
    });

    const viewToken = response?.viewToken;

    if (!viewToken) {
      return;
    }
    const dashboard = {
      title: newDashboard.name,
      viewToken,
    };
    showSuccessNotification({
      contentComponent: `This ${getAnalysisTypeDisplayName(
        useCase,
      )} is added to "${dashboard.title}"`,
      props: {
        isClosable: true,
        timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        action: <OpenDashboardLink dashboard={dashboard} />,
      },
    });
    onClose();
    trackDashboardEvent({
      eventName: 'New Dashboard Created With Analysis',
      dashboardLink: getDashboardLink(dashboard, tenantSlug),
      analysisLink: window.location.href,
    });
  };

  const isCreateDisabled =
    newDashboard.name.length === 0 ||
    newDashboard.name.length > DASHBOARD_NAME_MAX_LENGTH;

  return (
    <Box component="form" onSubmit={onCreate}>
      <CreateNewDashboardDialogTitle onClose={onClose} />
      <Divider />
      <CreateNewDashboardDialogContentForm
        dashboard={newDashboard}
        setDashboard={setNewDashboard}
        analysisName={analysisName}
      />
      <Divider />
      <CreateNewDashboardDialogActions
        onClose={onClose}
        isCreateDisabled={isCreateDisabled}
        isLoading={loading}
      />
    </Box>
  );
};
